import {Map as ImMap, List, RecordOf} from 'immutable';
import {createAction, ActionType} from '@packages/utilities/create-action';
import type {DesignerActionFromCreator} from '@packages/systems/designer/flux';
import type {RawCollection} from '@packages/systems/dynamo/utils/CollectionUtils';
import {
  DATABASE_CREATED,
  COLLECTIONS_RECEIVED,
  COLLECTION_ITEM_COUNTS_RECEIVED,
  COLLECTION_PRESET_APPLIED,
  DYN_COLLECTION_SUBMITTED,
  DYN_COLLECTION_DELETED,
  DYN_COLLECTION_MINTED,
  DYN_COLLECTION_DUPLICATED,
  DYN_COLLECTION_SAVED,
  DYN_ITEM_DELETED,
  DYN_ITEM_SAVED,
  DYN_ITEM_SAVED_AND_PUBLISHED,
  DYN_ITEM_UNSCHEDULE_SUCCEEDED,
  DYN_INLINE_ITEM_MINTED,
  DYN_ITEM_DESELECTED,
  DYN_ITEM_MINTED,
  DYN_ITEM_SELECTED,
  RAW_COLLECTION_RECEIVED,
  RAW_ITEMS_RECEIVED,
  DYN_COLLECTION_MODIFIED,
  DYN_FIELD_SUBMITTED,
  SAMPLE_ITEMS_GENERATION_SUCCEEDED,
  SCHEDULED_ITEMS_RECEIVED,
  ITEM_DUPLICATED,
  ITEM_REMOVED_REMOTELY,
  CANVAS_DYN_ITEM_SAVED,
  COLLECTION_REMOVED_REMOTELY,
  CSV_ITEMS_GENERATED,
  COMMERCE_STATUS_FETCH,
  CMS_COLLECTION_SORT_ENDED,
  DYN_ITEM_MODIFIED,
  CMS_ITEM_AUTOSAVE_CHANGES_SAVED,
  CMS_ITEM_DRAFT_CREATED,
  CMS_ITEM_DRAFT_CHANGES_STATUS_CHANGED,
  CMS_ITEM_DRAFT_STATUS_CHANGED,
  CMS_ITEM_QUEUED_STATUS_CHANGED,
  CMS_ITEM_FAILED_SCHEDULED_CHANGED,
  CMS_ITEM_SCHEDULED_CHANGED,
  CMS_ITEM_ARCHIVED_STATUS_CHANGED,
  DYN_ITEM_BATCH_MODIFIED,
} from '@packages/systems/designer/actions';
import type {Locale} from '@packages/systems/localization-types';
import {ItemRecordType} from '@packages/systems/core/records/ItemRecord';
import {CollectionRecordType} from '@packages/systems/dynamo/records/CollectionRecord';
import {
  completeBulkItemDrafting,
  completeBulkItemArchiving,
  completeBulkItemDeletion,
  completeBulkItemUnpublish,
  completeBulkItemDeletionRemotely,
} from '@packages/systems/designer/bulk-editing';
import {ScheduleItemsType} from '@packages/systems/core/records/CollectionStateType';
import {InventoryType, Variant} from '@packages/systems/commerce/core';
import {
  commerceConnected,
  commerceItemCreationRecoveryStarted,
  generateSampleProducts,
} from '@packages/systems/commerce/store';
import {ItemSourceType} from '@packages/systems/cms/types';
import {
  multiplayerCommerceConnectedRemotely,
  multiplayerProductItemsGeneratedRemotely,
} from '@packages/utilities/multiplayer-utils';

export type DynItemDeletedPayload = {
  item: ItemRecordType;
  collectionId: string;
  unscheduled?: boolean;
  skipApolloCacheReset?: boolean;
  locales?: Locale[];
  source?: ItemSourceType;
};

const commerceStatusFetched = createAction<
  typeof COMMERCE_STATUS_FETCH,
  {
    response: RecordOf<{
      ok: boolean;
      collections: ImMap<string, CollectionRecordType>;
    }>;
  }
>(COMMERCE_STATUS_FETCH);

export const collectionItemDeleted = createAction<
  typeof DYN_ITEM_DELETED,
  DynItemDeletedPayload
>(DYN_ITEM_DELETED);

export type ItemDeletedAction = DesignerActionFromCreator<
  typeof collectionItemDeleted
>;

export const collectionItemDeletedRemotely = createAction<
  typeof ITEM_REMOVED_REMOTELY,
  {itemId: string}
>(ITEM_REMOVED_REMOTELY);

export type CollectionItemDeletedRemotelyAction = DesignerActionFromCreator<
  typeof collectionItemDeletedRemotely
>;

export type DynItemSaveOrPublishPayload = {
  item: ItemRecordType;
  secondaryItems?: Array<{item: ItemRecordType; unscheduled?: boolean}>;
  collectionId?: string | null;
  oldItemId: string;
  oldItem: ItemRecordType;
  wasMint: boolean;
  fromInline: boolean;
  itemWithDependentsSavingInProgress?: boolean;
  itemSavingFinished?: boolean;
  scheduledTime?: string;
  scheduleInAllLocales?: boolean;
  isUnscheduling?: boolean;
  unscheduled?: boolean;
  skipPanelClose?: boolean;
  skipApolloCacheReset?: boolean;
  variantMintId?: string;
  inventoryType?: InventoryType;
  quantity?: number;
  locale?: Locale | null;
  source?: ItemSourceType;
  wasDMAutosave?: boolean;
};

export const saveDynItem = createAction<
  typeof DYN_ITEM_SAVED,
  DynItemSaveOrPublishPayload
>(DYN_ITEM_SAVED);

export type ItemSavedAction = DesignerActionFromCreator<typeof saveDynItem>;

export const saveDynItemAndPublish = createAction<
  typeof DYN_ITEM_SAVED_AND_PUBLISHED,
  DynItemSaveOrPublishPayload
>(DYN_ITEM_SAVED_AND_PUBLISHED);

export type ItemSavedAndPublishedAction = DesignerActionFromCreator<
  typeof saveDynItemAndPublish
>;

export const canvasDynItemSaved = createAction<
  typeof CANVAS_DYN_ITEM_SAVED,
  {
    itemId: string;
    collectionId: string;
    fields: {[slug: string]: any};
    skipApolloCacheReset?: boolean;
  }
>(CANVAS_DYN_ITEM_SAVED);

export type CanvasDynItemSavedAction = DesignerActionFromCreator<
  typeof canvasDynItemSaved
>;

export const collectionMinted = createAction<
  typeof DYN_COLLECTION_MINTED,
  {
    collection: CollectionRecordType;
  }
>(DYN_COLLECTION_MINTED);

export type CollectionMintedAction = DesignerActionFromCreator<
  typeof collectionMinted
>;

export const collectionItemDuplicated = createAction<
  typeof ITEM_DUPLICATED,
  {
    item: ItemRecordType;
    collectionId: string;
    duplicatedVariants?: Variant[];
    newDefaultVariant?: Variant;
  }
>(ITEM_DUPLICATED);

export type CollectionItemDuplicatedAction = DesignerActionFromCreator<
  typeof collectionItemDuplicated
>;

export const collectionDeleted = createAction<
  typeof DYN_COLLECTION_DELETED,
  {
    collection: CollectionRecordType;
  }
>(DYN_COLLECTION_DELETED);

export type CollectionDeletedAction = DesignerActionFromCreator<
  typeof collectionDeleted
>;

export const collectionDeletedRemotely = createAction<
  typeof COLLECTION_REMOVED_REMOTELY,
  {collectionId: string}
>(COLLECTION_REMOVED_REMOTELY);

export type CollectionDeletedRemotelyAction = DesignerActionFromCreator<
  typeof collectionDeletedRemotely
>;

export const collectionDuplicated = createAction<
  typeof DYN_COLLECTION_DUPLICATED,
  {
    collection: CollectionRecordType;
    sourceCollectionId: string;
  }
>(DYN_COLLECTION_DUPLICATED);

export type CollectionDuplicatedAction = DesignerActionFromCreator<
  typeof collectionDuplicated
>;

export const collectionSaved = createAction<
  typeof DYN_COLLECTION_SAVED,
  {
    collection: CollectionRecordType;
    oldCollectionId: string;
    /** When true, do not navigate away from collection settings to the items list. */
    skipItemsListNavigation?: boolean;
  }
>(DYN_COLLECTION_SAVED);

export type CollectionSavedAction = DesignerActionFromCreator<
  typeof collectionSaved
>;

export const collectionsReceived = createAction<
  typeof COLLECTIONS_RECEIVED,
  {
    collections: ImMap<string, CollectionRecordType>;
  }
>(COLLECTIONS_RECEIVED);

export type CollectionsReceivedAction = DesignerActionFromCreator<
  typeof collectionsReceived
>;

export const receiveCollectionItemCounts = createAction<
  typeof COLLECTION_ITEM_COUNTS_RECEIVED,
  {
    itemCountByCollectionId: ImMap<string, number>;
    localizedItemCountByLocaleId: ImMap<string, ImMap<string, number>>;
  }
>(COLLECTION_ITEM_COUNTS_RECEIVED);

export const collectionPresetApplied = createAction<
  typeof COLLECTION_PRESET_APPLIED,
  {
    preset: string;
  }
>(COLLECTION_PRESET_APPLIED);

export type CollectionPresetAppliedAction = DesignerActionFromCreator<
  typeof collectionPresetApplied
>;

export const collectionModified = createAction<
  typeof DYN_COLLECTION_MODIFIED,
  {keyPath: Array<string>; value: unknown}
>(DYN_COLLECTION_MODIFIED);

export type CollectionModifiedAction = DesignerActionFromCreator<
  typeof collectionModified
>;

export const collectionSubmitted = createAction<
  typeof DYN_COLLECTION_SUBMITTED
>(DYN_COLLECTION_SUBMITTED);

/**
 * Every limit is optional because SiteDataStore's reducer already treats a
 * missing one as "keep what the site load provided" — a Database the caller read
 * from Mongo can leave any of them unset. Omit an unknown limit rather than
 * passing null, which that reducer would store as the limit itself.
 */
export const databaseCreated = createAction<
  typeof DATABASE_CREATED,
  {
    database: {
      _id: string;
      maxFieldLimit?: number;
      maxItemRefs?: number;
      maxItemLimit?: number;
      maxEcomItemLimit?: number;
      maxLocales?: number;
      maxCollectionLimit?: number;
    };
  }
>(DATABASE_CREATED);

export type DatabaseCreatedAction = DesignerActionFromCreator<
  typeof databaseCreated
>;

export const dynFieldSubmitted =
  createAction<typeof DYN_FIELD_SUBMITTED>(DYN_FIELD_SUBMITTED);

export const receiveRawCollection = createAction<
  typeof RAW_COLLECTION_RECEIVED,
  {response: RawCollection}
>(RAW_COLLECTION_RECEIVED);

export type RawCollectionReceivedAction = DesignerActionFromCreator<
  typeof receiveRawCollection
>;

export const receiveRawItems = createAction<
  typeof RAW_ITEMS_RECEIVED,
  {
    items: ItemRecordType[];
    mergeExistingItemsTogether?: boolean;
    wasDMAutosave?: boolean;
  }
>(RAW_ITEMS_RECEIVED);

export type RawItemsReceivedAction = DesignerActionFromCreator<
  typeof receiveRawItems
>;

export const generateSampleItems = createAction<
  typeof SAMPLE_ITEMS_GENERATION_SUCCEEDED,
  {
    collectionId?: string;
    currentLocale?: Locale | null;
    itemsCreatedCount: number;
    firstItemId: string | null;
    itemIds?: Array<string>;
    source?: ItemSourceType;
  }
>(SAMPLE_ITEMS_GENERATION_SUCCEEDED);

export type SampleItemsGenerationSucceededAction = DesignerActionFromCreator<
  typeof generateSampleItems
>;

export const csvItemsGenerated = createAction<
  typeof CSV_ITEMS_GENERATED,
  {
    items: List<ItemRecordType>;
    collection: CollectionRecordType;
    hasNewFields: boolean;
    itemStatuses: List<'created' | 'updated'>;
    failedItems: List<any>;
    hasReferences: boolean;
    referenceType: 'none' | 'both' | 'single' | 'multi';
    columnsLinkedToReferenceFields: 'none' | 'both' | 'single' | 'multi';
  }
>(CSV_ITEMS_GENERATED);

export type CSVItemsGeneratedAction = DesignerActionFromCreator<
  typeof csvItemsGenerated
>;

export const receiveScheduledItems = createAction<
  typeof SCHEDULED_ITEMS_RECEIVED,
  {scheduledItems: ScheduleItemsType}
>(SCHEDULED_ITEMS_RECEIVED);

export const mintDynItem = createAction<
  typeof DYN_ITEM_MINTED,
  {
    item: ItemRecordType;
    collectionId: string;
    currentCollection?: CollectionRecordType;
    initialVariantMintId: `mint-${string}`;
    source?: ItemSourceType;
  }
>(DYN_ITEM_MINTED);

export type DynItemMintedAction = DesignerActionFromCreator<typeof mintDynItem>;

export const mintDynInlineItem = createAction<
  typeof DYN_INLINE_ITEM_MINTED,
  {
    item: ItemRecordType;
    collectionId: string;
    fieldSlug: string;
    initialVariantMintId: string;
  }
>(DYN_INLINE_ITEM_MINTED);

export type DynInlineItemMintedAction = DesignerActionFromCreator<
  typeof mintDynInlineItem
>;

export const selectDynItem = createAction<
  typeof DYN_ITEM_SELECTED,
  {
    id: string;
    item: ItemRecordType;
  }
>(DYN_ITEM_SELECTED);

export type DynItemSelectedAction = DesignerActionFromCreator<
  typeof selectDynItem
>;

export const deselectDynItem = createAction<
  typeof DYN_ITEM_DESELECTED,
  {
    item: ItemRecordType;
    skipPanelClose?: boolean;
    source?: ItemSourceType;
  }
>(DYN_ITEM_DESELECTED);

export type DynItemDeSelectedAction = DesignerActionFromCreator<
  typeof deselectDynItem
>;

export const collectionItemUnscheduled = createAction<
  typeof DYN_ITEM_UNSCHEDULE_SUCCEEDED,
  {request: {meta: ImMap<'item', ItemRecordType>}}
>(DYN_ITEM_UNSCHEDULE_SUCCEEDED);

export type CollectionItemUnscheduleSucceededAction = DesignerActionFromCreator<
  typeof collectionItemUnscheduled
>;

export const cmsCollectionSortEnded = createAction<
  typeof CMS_COLLECTION_SORT_ENDED,
  {
    collectionId: string;
    targetIndex: number;
    previousIndex: number;
    collections: string[];
  }
>(CMS_COLLECTION_SORT_ENDED);

export type CmsCollectionSortEndedAction = DesignerActionFromCreator<
  typeof cmsCollectionSortEnded
>;

export const cmsItemAutosaveChangesSaved = createAction<
  typeof CMS_ITEM_AUTOSAVE_CHANGES_SAVED,
  {
    source?: ItemSourceType;
  }
>(CMS_ITEM_AUTOSAVE_CHANGES_SAVED);

export const cmsItemDraftCreated = createAction<
  typeof CMS_ITEM_DRAFT_CREATED,
  {
    source?: ItemSourceType;
  }
>(CMS_ITEM_DRAFT_CREATED);

export const cmsItemDraftStatusChanged = createAction<
  typeof CMS_ITEM_DRAFT_STATUS_CHANGED,
  {
    source?: ItemSourceType;
  }
>(CMS_ITEM_DRAFT_STATUS_CHANGED);

export const cmsItemQueuedStatusChanged = createAction<
  typeof CMS_ITEM_QUEUED_STATUS_CHANGED,
  {
    source?: ItemSourceType;
  }
>(CMS_ITEM_QUEUED_STATUS_CHANGED);

export const cmsItemDraftChangesStatusChanged = createAction<
  typeof CMS_ITEM_DRAFT_CHANGES_STATUS_CHANGED,
  {
    source?: ItemSourceType;
  }
>(CMS_ITEM_DRAFT_CHANGES_STATUS_CHANGED);

export const cmsItemFailedScheduleChanged = createAction<
  typeof CMS_ITEM_FAILED_SCHEDULED_CHANGED,
  {
    source?: ItemSourceType;
  }
>(CMS_ITEM_FAILED_SCHEDULED_CHANGED);

export const cmsItemScheduledChanged = createAction<
  typeof CMS_ITEM_SCHEDULED_CHANGED,
  {
    source?: ItemSourceType;
  }
>(CMS_ITEM_SCHEDULED_CHANGED);

export const cmsItemArchivedStatusChanged = createAction<
  typeof CMS_ITEM_ARCHIVED_STATUS_CHANGED,
  {
    source?: ItemSourceType;
  }
>(CMS_ITEM_ARCHIVED_STATUS_CHANGED);

export const dynItemModified = createAction<
  typeof DYN_ITEM_MODIFIED,
  {
    keyPath: Array<string | number>;
    value: any;
    formId: string;
    recordId: string;
    meta?: Partial<{
      validationFailure: boolean;
      isImageSizeTooSmall: boolean;
      isImageSizeTooLarge: boolean;
      isImageWidthTooSmall: boolean;
      isImageWidthTooLarge: boolean;
      isImageHeightTooSmall: boolean;
      isImageHeightTooLarge: boolean;
    }>;
  }
>(DYN_ITEM_MODIFIED);

export const dynItemBatchModified = createAction<
  typeof DYN_ITEM_BATCH_MODIFIED,
  {
    formId: string;
    recordId: string;
    fieldUpdates: Array<{
      keyPath: Array<string | number>;
      value: any;
    }>;
  }
>(DYN_ITEM_BATCH_MODIFIED);

export type DynItemModifiedAction = DesignerActionFromCreator<
  typeof dynItemModified
>;

export type DynItemBatchModifiedAction = DesignerActionFromCreator<
  typeof dynItemBatchModified
>;

export type CollectionAction = ActionType<
  | typeof receiveRawCollection
  | typeof receiveCollectionItemCounts
  | typeof collectionsReceived
  | typeof receiveRawItems
  | typeof receiveScheduledItems
  | typeof cmsCollectionSortEnded
  | typeof collectionDeleted
  | typeof collectionDeletedRemotely
  | typeof collectionMinted
  | typeof collectionDuplicated
  | typeof collectionSaved
  | typeof commerceConnected
  | typeof commerceItemCreationRecoveryStarted
  | typeof commerceStatusFetched
  | typeof csvItemsGenerated
  | typeof collectionItemDeleted
  | typeof collectionItemDeletedRemotely
  | typeof collectionItemUnscheduled
  | typeof saveDynItem
  | typeof saveDynItemAndPublish
  | typeof canvasDynItemSaved
  | typeof completeBulkItemDeletionRemotely
  | typeof completeBulkItemUnpublish
  | typeof mintDynItem
  | typeof mintDynInlineItem
  | typeof collectionItemDuplicated
  | typeof selectDynItem
  | typeof completeBulkItemDrafting
  | typeof completeBulkItemArchiving
  | typeof completeBulkItemDeletion
  | typeof multiplayerCommerceConnectedRemotely
  | typeof generateSampleItems
  | typeof generateSampleProducts
  | typeof multiplayerProductItemsGeneratedRemotely
>;
